import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { AuthNavbar } from "../../../components/auth/AuthNavbar";
import { BiArrowBack } from "react-icons/bi";
import { CiImageOn } from "react-icons/ci";
import bx_edit_icon from "../../../assets/bx_edit.svg";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { CustomButton } from "../../../components/button/CustomButton";
import { LocationAutocomplete } from "../../../components/location/LocationAutocomplete";
import { SignUpSchema } from "./validation/SignUpSchema";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { signupUserThunk as onSignUpUser } from "../../../slices/users/thunk";
import { resetFlags } from "../../../slices/users/reducer";
import { Spinner } from "../../../components/spinner/Spinner";

// Initial Values
const initialValues = {
  name: "",
  email: "",
  location: "",
  locationLat: "",
  locationLng: "",
  password: "",
  confirmPassword: "",
  image: null,
  experience: "",
  skills: [],
  bio: "",
  refferel_code: "",
};

export const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [image, setImage] = useState(null);
  const [isRefferalUser, setIsRefferalUser] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema: SignUpSchema(role),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("id", 0);
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("location_name", values.location);
      formData.append("location_lat", values.locationLat);
      formData.append("location_lang", values.locationLng);
      formData.append("user_type", role === "user" ? 1 : 2);
      formData.append("password", values.password);
      formData.append("password_confirmation", values.confirmPassword);
      formData.append("referral_code", values.refferel_code);

      if (values.refferel_code) {
        setIsRefferalUser(true);
      }

      if (values.image) {
        formData.append("image", values.image);
      }
      if (role == "artist") {
        formData.append("experince", values.experience);
        formData.append("bio", values.bio);
        formData.append("skills", JSON.stringify(values.skills));
      }

      dispatch(onSignUpUser(formData));
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
  } = formik;

  const handleImageChange = (e) => {
    const file = e.currentTarget.files[0];
    if (file) {
      setFieldValue("image", file);
      setImage(URL.createObjectURL(file));
    }
  };

  const selectUserState = (state) => state.user;

  const userProperties = createSelector(selectUserState, (ecom) => ({
    isSuccess: ecom.isSuccess,
    isLoading: ecom.isLoading,
    userId: ecom.userId,
  }));

  const { isLoading, isSuccess, userId } = useSelector(userProperties);

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetFlags());
      if (isRefferalUser) {
        navigate("/user");
      } else {
        navigate(`/pricing/${role}/${userId}`);
      }
    }
  }, [isSuccess, userId, navigate, dispatch, isRefferalUser]);

  return (
    <div className="w-full min-h-screen bg-[#F7F7F7]">
      <AuthNavbar />
      <div className="pt-20 sm:pt-28 px-4 sm:px-10">
        <div className="w-[50px] h-[50px] bg-transparent rounded-[10px] border border-border_color flex items-center justify-center cursor-pointer">
          <button onClick={() => navigate("/user-type")}>
            <BiArrowBack className="text-4xl" />
          </button>
        </div>
      </div>
      <div className="w-full flex items-center justify-center p-4">
        <div className="w-full max-w-[641px] border border-border_color rounded-3xl p-4 sm:p-10 mb-10">
          <form onSubmit={handleSubmit}>
            {/* Profile Image */}
            <div className="w-full flex items-center justify-center">
              <div className="relative w-[162px] h-[162px]">
                <div className="w-full h-full rounded-full bg-white overflow-hidden flex items-center justify-center">
                  {image ? (
                    <img
                      src={image}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-500">
                      <CiImageOn className="text-8xl text-gray-300" />
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  name="image"
                  id="profileUpload"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label
                  htmlFor="profileUpload"
                  className="absolute bottom-1 right-1 rounded-full cursor-pointer"
                >
                  <div className="w-[30px] h-[30px]">
                    <img
                      src={bx_edit_icon}
                      alt="Edit"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </label>
              </div>
              {errors.image && touched.image ? (
                <p className="text-red-600 text-[12px]">{errors.image}</p>
              ) : null}
            </div>
            <div className="w-full mt-8">
              {/* Name */}
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                >
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Enter your name"
                />
                {errors.name && touched.name && (
                  <p className="text-red-600 text-[12px]">{errors.name}</p>
                )}
              </div>
              {/* Email */}
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Enter your email"
                />
                {errors.email && touched.email && (
                  <p className="text-red-600 text-[12px]">{errors.email}</p>
                )}
              </div>
              {/* Refferel Code */}
              {role === "user" && (
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                  >
                    Refferel Code (Optional)
                  </label>
                  <input
                    type="text"
                    name="refferel_code"
                    value={values.refferel_code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    placeholder="Enter Refferel Code"
                  />
                </div>
              )}
              {/* Location */}
              <div className="mb-4">
                <label
                  htmlFor="location"
                  className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                >
                  Location
                </label>
                <LocationAutocomplete
                  value={formik.values.location}
                  onChange={(val) => formik.setFieldValue("location", val)}
                  onSelect={(place) => {
                    formik.setFieldValue("location", place.address || "");
                    formik.setFieldValue("locationLat", place.lat || "");
                    formik.setFieldValue("locationLng", place.lng || "");
                    formik.setFieldValue(
                      "locationPlaceId",
                      place.place_id || ""
                    );
                  }}
                />
                {formik.errors.location && formik.touched.location && (
                  <p className="text-red-600 text-sm">
                    {formik.errors.location}
                  </p>
                )}
              </div>
              {/* Password */}
              <div className="mb-4 relative">
                <label
                  htmlFor="password"
                  className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                >
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Enter your password"
                />
                <div
                  className="absolute right-3 top-12 cursor-pointer text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                {errors.password && touched.password && (
                  <p className="text-red-600 text-[12px]">{errors.password}</p>
                )}
              </div>
              {/* Confirm Password */}
              <div className="mb-4 relative">
                <label
                  htmlFor="confirmPassword"
                  className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                >
                  Confirm Password
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Confirm your password"
                />
                <div
                  className="absolute right-3 top-12 cursor-pointer text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="text-red-600 text-[12px]">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
              {/* Artist-specific fields */}
              {role === "artist" && (
                <>
                  {/* Experience */}
                  <div className="mb-4">
                    <label
                      htmlFor="experience"
                      className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                    >
                      Experience
                    </label>
                    <input
                      type="text"
                      name="experience"
                      value={values.experience}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="w-full px-3 py-[14px] bg-white border text-[#201D1D] text-[16px] font-medium font-grotesk border-border_color rounded-xl outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      placeholder="Enter your experience"
                    />
                    {errors.experience && touched.experience && (
                      <p className="text-red-600 text-sm">
                        {errors.experience}
                      </p>
                    )}
                  </div>
                  {/* Skills */}
                  <div className="mb-4">
                    <label
                      htmlFor="skills"
                      className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                    >
                      Skills
                    </label>
                    <div className="w-full bg-white border rounded-xl p-2">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {values.skills.map((skill, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 border px-3 py-1 rounded-3xl text-sm font-bold"
                          >
                            {skill}
                            <button
                              type="button"
                              className="ml-1 text-black hover:text-red-700"
                              onClick={() =>
                                setFieldValue(
                                  "skills",
                                  values.skills.filter((s) => s !== skill)
                                )
                              }
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      <textarea
                        rows={4}
                        placeholder="Type your skill (press enter)"
                        className="w-full resize-none focus:outline-none"
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            e.target.value.trim() !== ""
                          ) {
                            e.preventDefault();
                            const newSkill = e.target.value.trim();
                            if (!values.skills.includes(newSkill)) {
                              setFieldValue("skills", [
                                ...values.skills,
                                newSkill,
                              ]);
                            }
                            e.target.value = "";
                          }
                        }}
                      ></textarea>
                    </div>
                    {errors.skills && touched.skills && (
                      <p className="text-red-600 text-sm">{errors.skills}</p>
                    )}
                  </div>
                  {/* Bio */}
                  <div className="mb-4">
                    <label
                      htmlFor="bio"
                      className="block text-lable_color font-grotesk text-[16px] font-bold mb-1"
                    >
                      Bio
                    </label>
                    <textarea
                      name="bio"
                      rows={4}
                      value={values.bio}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="w-full resize-none p-3 rounded-xl border border-border_color text-[#201D1D] text-[16px] font-medium font-grotesk focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      placeholder="Write something about yourself..."
                    ></textarea>
                    {errors.bio && touched.bio && (
                      <p className="text-red-600 text-sm">{errors.bio}</p>
                    )}
                  </div>
                </>
              )}
              <CustomButton
                text={isLoading ? <Spinner /> : "Sign Up"}
                type="submit"
                className="mt-5"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
